import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
// import styles from './GameReports.module.scss'; // Removing SCSS import
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Pagination, Stack } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { deleteGamePost, getFetchGamePostFileThumbnail, getVideos, markGamePostFav } from '../../../redux/network/videoApi';
import { useLocation, useNavigate } from 'react-router-dom';
import FullScreenDialog from '../../VideoPlayerModel/VideoPlayerModel';
import thumbnail from './thumbnail.jpg';
import { deleteVideoPost, toggleFavVideoPost } from '../../../redux/reducers/videoSlice';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { setAlertMessage, setAlertType } from '../../../redux/reducers/alertSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlay } from '@fortawesome/free-regular-svg-icons';
import CustomDropdown from '../../CustomeDropdown/CustomDropdown';
import CoachFeedback from '../../CoachFlow/CoachFeedback';
import CloseIcon from '@mui/icons-material/Close';
import SearchVideos from "../../SearchBoxes/SearchVideos";
import { SEARCH_BOX, COACH_NOT_ASSIGNED } from "../../../constant";
import SearchUploadedVideoPagination from '../../Pagination/SearchUploadedVideoPagination/SearchUploadedVideoPagination';
import CoachName from '../CoachName/CoachName';
import { usePagination } from '../../../hooks/usePagination';
import ReactGA from 'react-ga4';
import TagHelper from '../../../helpers/PostHelpers/TagHelper';

// Set limit to 12
const VIDEO_SEARCH_API_LIMIT = 12;

// VideoJS deprecation warnings are suppressed globally in utils/videojsWarnings.js

const extractTitle = (videoPath) => {
    if (!videoPath) return "Unknown Title";
    const filename = videoPath.split('/').pop().replace(/\.[^/.]+$/, "");
    return filename;
};

const truncateTitle = (title, maxWords = 6) => {
    if (!title) return "";
    const words = title.split(" ");
    return words.length > maxWords ? words.slice(0, maxWords).join(" ") + " ..." : title;
};

const dataFormatter = (dateString) => {
    const inputDate = new Date(dateString);

    const monthNames = [
        "January", "February", "March",
        "April", "May", "June", "July",
        "August", "September", "October",
        "November", "December"
    ];

    const day = inputDate.getDate();
    const monthIndex = inputDate.getMonth();
    const year = inputDate.getFullYear();
    const hours = inputDate.getHours();
    const minutes = inputDate.getMinutes();

    return `${day} ${monthNames[monthIndex]} ${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Helper function to calculate the start and end of time ranges
const getTimeRange = (range) => {
    const now = new Date();
    let start, end;
    switch (range) {
        case 'lastOneWeek':
            start = new Date(now.setDate(now.getDate() - 7));
            end = new Date();
            break;
        case 'lastMonth':
            start = new Date(now.setMonth(now.getMonth() - 1));
            end = new Date();
            break;
        case 'last3Months':
            start = new Date(now.setMonth(now.getMonth() - 3));
            end = new Date();
            break;
        default:
            start = null;
            end = null;
    }
    return { start: start?.getTime(), end: end?.getTime() };
};

/*const filterOptions = [
    { value: 'reviewed', label: 'Completed' },
    { value: 'favourites', label: 'Favourites' },
];*/

const sortOptions = [
    { value: 'feedbackPriority', label: 'Sort By' },
    { value: 'newToOld', label: 'New to Old' },
    { value: 'oldToNew', label: 'Old to New' },
];

const GameReports = () => {
    const env = process.env.REACT_APP_ENV;
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Redux selectors
    const { isStarted, isError, error, data, totalNumberOfRecords } = useSelector(state => state.video.video);
    const userData = useSelector((state) => state.user.profile);

    // Custom pagination hook
    const {
        currentPage,
        totalPages,
        totalRecords,
        searchTerm,
        sortOption,
        goToPage,
        updateSearchTerm,
        updateSortOption
    } = usePagination('gameReports');

    // Local state
    const [videoThumbnails, setVideoThumbnails] = useState({});
    const [thumbnailLoading, setThumbnailLoading] = useState({});
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [animatedFavs, setAnimatedFavs] = useState({});
    const [deletedPostId, setDeletedPostId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [filterOption, setFilterOption] = useState('reviewed');
    const [coachName, setCoachName] = useState(null);
    const [coachNameKey, setCoachNameKey] = useState(0);

    // Debug tracking
    const prevApiPayload = useRef(null);
    const thumbnailsLoadedRef = useRef(false);

    // Derived state
    const jwtToken = localStorage.getItem("BQS_TOKEN");
    const userId = localStorage.getItem("BQS__ID");
    const remainingReview = userData?.value?.profileData?.membershipProfile?.remainingReviews || 0;

    const isDev = ["development", "local", "poc", "users"].includes(env);

    // GA: page view for Game Reports
    useEffect(() => {
        try {
            ReactGA.send({ hitType: 'pageview', page: pathname, title: 'Game Reports' });
        } catch { }
    }, [pathname]);

    useEffect(() => {
        setCoachName(userData?.value?.roleAssociation?.associates[0]?.profileData?.name ?? COACH_NOT_ASSIGNED);
        setCoachNameKey((prev) => prev + 1);
    }, [userData]);

    // Get sort configuration - memoized to prevent unnecessary re-renders
    const sortConfig = useMemo(() => {
        switch (sortOption) {
            case 'feedbackPriority':
                return {
                    "coachManualFeedback": 1, // Empty feedback first (1 = ascending, empty strings come first)
                    "updatedAt": -1
                };
            case 'newToOld':
                return { "updatedAt": -1 };
            case 'oldToNew':
                return { "updatedAt": 1 };
            default:
                return {
                    "coachManualFeedback": 1, // Default: Empty feedback first
                    "updatedAt": -1
                };
        }
    }, [sortOption]);

    // Memoize API payload to prevent unnecessary API calls
    const apiPayload = useMemo(() => {
        if (!jwtToken || !userId || env === "standalone") {
            return null;
        }

        const conditions = { "poster": userId };

        // Always filter for reviewed videos only in Reports section
        if (filterOption === "reviewed") {
            conditions.state = ["reviewed"];
        } else if (filterOption === "favourites") {
            conditions.state = ["reviewed"];
            conditions.fav = true;
        }

        conditions.searchTerm = searchTerm;

        const payload = {
            "condition": conditions,
            "sort": sortConfig,
            "skip": currentPage - 1,
            "limit": VIDEO_SEARCH_API_LIMIT,
        };

        console.warn('🔄 apiPayload updated:', { currentPage, skip: payload.skip, limit: payload.limit });
        return payload;
    }, [jwtToken, userId, filterOption, searchTerm, sortConfig, currentPage, env, isDev]);

    // Fetch videos - only when apiPayload changes
    useEffect(() => {
        if (apiPayload) {
            console.warn('📡 GameReports API call triggered:', apiPayload);
            dispatch(getVideos(jwtToken, apiPayload));
        }
    }, [apiPayload, jwtToken, dispatch]);

    // Explicit trigger when currentPage changes (for pagination)
    useEffect(() => {
        if (currentPage && apiPayload && jwtToken) {
            console.warn('📄 Page changed to:', currentPage, '- Fetching videos...');
            dispatch(getVideos(jwtToken, apiPayload));
        }
    }, [currentPage]); // Only trigger on currentPage change

    // Force fresh API call when Reports section is opened
    useEffect(() => {
        if (pathname.includes('/game-reports') && apiPayload && jwtToken) {
            console.warn('🔄 Fresh fetch on Reports section open');
            dispatch(getVideos(jwtToken, apiPayload));
        }
    }, [pathname]); // Only run when pathname changes (user navigates to Reports)

    // Update pagination state when video data changes - optimized
    useEffect(() => {
        if (totalNumberOfRecords !== undefined) {
            const totalRecords = totalNumberOfRecords;
            const totalPages = Math.ceil(totalRecords / VIDEO_SEARCH_API_LIMIT);

            console.log('🔄 GameReports updating pagination:', {
                totalRecords,
                totalPages,
                currentPage,
                searchTerm,
                sortOption,
                component: 'gameReports'
            });

            // Update Redux state directly without using the hook function
            dispatch({
                type: 'pagination/updatePaginationFromResponse',
                payload: {
                    totalRecords,
                    currentPage,
                    searchTerm,
                    sortOption,
                    component: 'gameReports'
                }
            });
        }
    }, [totalNumberOfRecords, currentPage, searchTerm, sortOption, dispatch]);

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage]);

    // Load thumbnails - optimized to prevent infinite loops
    useEffect(() => {
        if (data?.length > 0 && jwtToken && env !== "standalone") {
            // Check if we've already loaded thumbnails for this data
            const dataHash = JSON.stringify(data.map(v => v._id));
            if (thumbnailsLoadedRef.current === dataHash) {
                return;
            }

            const loadThumbnails = async () => {
                // Only load thumbnails for videos that don't already have them
                const videosNeedingThumbnails = data.filter(video => !videoThumbnails[video._id]);

                if (videosNeedingThumbnails.length === 0) {
                    thumbnailsLoadedRef.current = dataHash;
                    return;
                }

                const loadingStates = {};
                const newThumbnails = { ...videoThumbnails };

                // Set loading states for videos that need thumbnails
                videosNeedingThumbnails.forEach(video => {
                    loadingStates[video._id] = true;
                });
                setThumbnailLoading(prev => ({ ...prev, ...loadingStates }));

                // Fetch thumbnails in parallel
                const thumbnailPromises = videosNeedingThumbnails.map(async (video) => {
                    try {
                        const thumbnailUrl = await getFetchGamePostFileThumbnail(
                            jwtToken,
                            video._id,
                            video.game[0]?.tnPath
                        );
                        return { id: video._id, url: thumbnailUrl };
                    } catch (error) {
                        return { id: video._id, url: null };
                    }
                });

                const results = await Promise.all(thumbnailPromises);

                // Update thumbnails
                results.forEach(({ id, url }) => {
                    if (url) {
                        newThumbnails[id] = url;
                    }
                });

                setVideoThumbnails(newThumbnails);

                // Mark as loaded for this data
                thumbnailsLoadedRef.current = dataHash;

                // Clear loading states
                const clearLoadingStates = {};
                Object.keys(loadingStates).forEach(id => {
                    clearLoadingStates[id] = false;
                });
                setThumbnailLoading(prev => ({ ...prev, ...clearLoadingStates }));
            };

            loadThumbnails();
        }
    }, [data, jwtToken, env]); // Removed videoThumbnails from dependencies to prevent infinite loop

    // Filter videos for standalone mode - memoized to prevent unnecessary recalculations
    const filteredVideos = useMemo(() => {
        if (!data) return [];

        if (env === "standalone") {
            return data.filter(video => video.state === "reviewed");
        }

        // All video types are treated equally - API already filters for state: ["reviewed"]
        // No special Match Video logic needed
        return data;
    }, [env, data]);

    function RenderNoVideosView() {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4">
                <div className="text-center max-w-md">
                    <div className="mb-6 inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100">
                        <svg className="w-12 h-12 text-[#247E45]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <h6 className="text-xl font-semibold text-slate-700 mb-2">No Reports Yet</h6>
                    <p className="text-slate-500">Once your gameplay report is generated, it will appear here.</p>
                </div>
            </div>
        );
    }

    function RenderNoVideosWithSearchCondition({ filterOption }) {
        let searchKey;
        if (filterOption === "reviewed") searchKey = "Completed";
        else if (filterOption === "favourites") searchKey = "Favourites";

        return (
            <div className="flex flex-col items-center justify-center py-20 px-4">
                <div className="text-center max-w-md">
                    <div className="mb-6 inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-amber-100 to-orange-100">
                        <svg className="w-12 h-12 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <h5 className="text-xl font-semibold text-slate-700 mb-2">No results found</h5>
                    <p className="text-slate-500">No reports found for filter: {searchKey}</p>
                </div>
            </div>
        );
    }

    function handleClickViewReport(postData) {
        localStorage.setItem("reviewPostData", JSON.stringify(postData));
        navigate("/game-feedback/coach-feedback");
    }

    async function handleClickFav(postData) {
        const postId = postData._id;
        const favToggle = !postData.fav;
        try { ReactGA.event({ category: 'GameReports', action: 'toggle_favourite', label: favToggle ? 'set' : 'unset' }); } catch { }
        const res = await markGamePostFav(jwtToken, postId, favToggle);

        if (res && res.code === 200) {
            setAnimatedFavs(prev => ({ ...prev, [postId]: true }));
            dispatch(toggleFavVideoPost(postId));

            setTimeout(() => {
                setAnimatedFavs(prev => ({ ...prev, [postId]: false }));
            }, 300);
        } else {
            dispatch(setAlertMessage(res.message || "Something went wrong"));
            dispatch(setAlertType("error"));
        }
    }

    const handleClickDel = (postData) => {
        setSelectedPost(postData);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedPost) return;

        setOpenDeleteDialog(false);

        if (env === "standalone") {
            return null;
        }

        const res = await deleteGamePost(jwtToken, selectedPost._id);

        if (res && res.code === 200) {
            try { ReactGA.event({ category: 'GameReports', action: 'delete_video_success' }); } catch { }
            setDeletedPostId(selectedPost._id);
            setTimeout(() => {
                dispatch(deleteVideoPost(selectedPost._id));
                setDeletedPostId(null);

                // If we're on a page with only one video and it's not the first page, go to previous page
                if (data?.length === 1 && currentPage > 1) {
                    goToPage(currentPage - 1);
                }
            }, 800);
        } else {
            dispatch(setAlertMessage(res.message || "Something went wrong"));
            dispatch(setAlertType("error"));
        }
    };

    // Handle video click - memoized to prevent unnecessary re-renders
    const handlePlayClick = useCallback((video, isOpen = true) => {
        const postId = video._id;
        const videoLinkInObjectStore = video.game[0].path;
        localStorage.setItem("videoLinkInObjectStore", videoLinkInObjectStore);
        localStorage.setItem("reviewPostId", postId);
        localStorage.setItem(`prevPath_${postId}`, JSON.stringify(pathname));

        const persistedTags = TagHelper.getPersistedTags(postId, video?.game?.[0]?.path);
        const apiTags = Array.isArray(video.tags) ? video.tags : [];
        const effectiveVideoTags = TagHelper.mergeTagLists(persistedTags, apiTags);
        const hydratedVideo = {
            ...video,
            tags: effectiveVideoTags,
        };

        setSelectedVideo(hydratedVideo);
        localStorage.setItem("reviewPost", JSON.stringify(hydratedVideo));
        localStorage.setItem(`${postId}_reviewPost`, JSON.stringify(hydratedVideo));
        localStorage.setItem("prevPath", JSON.stringify(pathname));
        TagHelper.persistTags(postId, video?.game?.[0]?.path, effectiveVideoTags);
        navigate(`feedback/${postId}`, { state: { videoTags: effectiveVideoTags } });

        if (env === "standalone") {
            if (video.state === "reviewed") {
                navigate(`feedback/${postId}`, { state: { videoTags: video.tags } });
            } else {
                setOpen(isOpen);
            }
            return null;
        }
    }, [env, navigate, pathname]);

    const handleFilterChange = (newFilterOption) => {
        setFilterOption(newFilterOption);
    };

    // GA-wrapped sort/search/pagination
    const handleSortChange = (val) => {
        try { ReactGA.event({ category: 'GameReports', action: 'sort_change', label: val }); } catch { }
        updateSortOption(val);
        goToPage(1);
    };

    const handleSearchChange = (text) => {
        try { ReactGA.event({ category: 'GameReports', action: 'search_change', label: text ? 'non_empty' : 'empty' }); } catch { }
        updateSearchTerm(text);
    };

    const handlePageChange = (page) => {
        try { ReactGA.event({ category: 'GameReports', action: 'page_change', label: String(page) }); } catch { }
        goToPage(page);
        window.scrollTo(0, 0);
    };

    const openModal = (video) => {
        setSelectedVideo(video);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedVideo(null);
        setIsModalOpen(false);
    };

    return (
        <>
            {/* Main Container with Gradient Background */}
            <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">

                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-30 pointer-events-none">
                    <div className="absolute top-0 -left-4 w-72 h-72 bg-emerald-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
                    <div className="absolute top-0 -right-4 w-72 h-72 bg-teal-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
                    <div className="absolute -bottom-8 left-20 w-72 h-72 bg-cyan-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
                </div>

                {/* FullScreen Video Player Dialog */}
                <FullScreenDialog open={open} setOpen={setOpen} selectedVideo={selectedVideo} />

                {/* Header Section */}
                <div className="relative z-10 flex items-center justify-between gap-4 mt-24 lg:mt-32 mb-8 px-4 md:px-6">
                    <div className="flex-1 min-w-0">
                        <h1
                            className="text-3xl md:text-5xl font-extrabold text-[#247E45] mb-2 drop-shadow-sm truncate"
                        >
                            My Reports
                        </h1>
                    </div>
                    {/* CoachName Badge - Positioned on Right */}
                    {coachName && coachNameKey > 0 && <CoachName coachName={coachName} setCoachNameKey={coachNameKey} />}
                </div>

                {/* Search & Filter Controls */}
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-3 mb-8 px-6">
                    {/* Search Bar Group */}
                    <div className="flex-1 w-full md:w-auto">
                        <SearchVideos
                            setSearchText={handleSearchChange}
                            existingSearchText={searchTerm}
                            placeholderText={SEARCH_BOX.VIDEO_SEARCH.PLAYER_PERSPECTIVE.PLACEHOLDER_TEXT}
                            className="w-full"
                        />
                    </div>

                    {/* Dropdown & Count Group */}
                    <div className="flex items-center gap-3">
                        <div className="w-[180px]">
                            <CustomDropdown
                                options={sortOptions}
                                selectedValue={sortOption}
                                onChange={handleSortChange}
                                label=""
                            />
                        </div>

                        {/* Total Videos Badge - Compact Size with Equal Height */}
                        {/* <div className="flex items-center gap-2 px-3 h-12 rounded-xl bg-[#247E45] text-white shadow-md hover:shadow-lg transition-all duration-300">
                             <span className="text-sm font-semibold whitespace-nowrap">Total Reports</span>
                             <span className="px-3 py-0.5 bg-white text-[#247E45] rounded-full text-sm font-bold min-w-[45px] text-center">
                                 {totalRecords || 0}
                             </span>
                         </div>*/}
                    </div>
                </div>

                {/* Videos Grid Section */}
                <div className="relative z-10 px-6 pb-20">
                    {jwtToken && (
                        <>
                            {isStarted ? (
                                <Box sx={{ display: 'flex', justifyContent: "center", alignItems: "center", height: "40vh" }}>
                                    <div className="flex flex-col items-center gap-4">
                                        <CircularProgress sx={{ color: '#247E45' }} thickness={4} />
                                        <p className="text-slate-500 font-medium">Loading reports...</p>
                                    </div>
                                </Box>
                            ) : isError ? (
                                <Box sx={{ display: 'flex', justifyContent: "center", alignItems: "center", height: "40vh" }}>
                                    <div className="text-center p-8 bg-red-50 rounded-2xl border border-red-100">
                                        <p className="text-red-600 font-semibold mb-2">Unable to load reports</p>
                                        <p className="text-red-500 text-sm">{error}</p>
                                    </div>
                                </Box>
                            ) : filteredVideos.length > 0 ? (
                                <div>
                                    {/* Premium Video Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                                        {filteredVideos.map((ele, index) => {
                                            const thumbnailUrl = videoThumbnails[ele._id] || thumbnail;
                                            const isFavAnimated = animatedFavs[ele._id];
                                            const isLoading = thumbnailLoading[ele._id];

                                            return (
                                                <div
                                                    key={ele._id}
                                                    className={`group relative flex flex-col rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-slate-200 ${deletedPostId === ele._id ? 'opacity-0 scale-95' : ''}`}
                                                >
                                                    {/* Card Header with Meta Info */}
                                                    <div className="flex items-start justify-between p-4 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                                <p className="text-xs font-medium text-slate-500 truncate">
                                                                    {dataFormatter(ele.updatedAt)}
                                                                </p>
                                                            </div>
                                                            <p className="text-sm font-semibold text-slate-800 truncate border-l-4 border-[#026B33] pl-2">
                                                                {truncateTitle(extractTitle(ele.game[0]?.path))}
                                                            </p>
                                                        </div>

                                                        {/* Action Icons */}
                                                        <div className="flex items-center gap-1 ml-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleClickFav(ele);
                                                                }}
                                                                className={`w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-all duration-200 ${isFavAnimated ? 'animate-pulse scale-110' : ''}`}
                                                                title={ele.fav ? "Remove from favourites" : "Add to favourites"}
                                                            >
                                                                <i className={`fa-${ele.fav ? 'solid' : 'regular'} fa-bookmark text-[#247E45] text-[20px]`}></i>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleClickDel(ele);
                                                                }}
                                                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 transition-all duration-200 group/delete"
                                                                title="Delete video"
                                                            >
                                                                <DeleteForeverIcon
                                                                    sx={{ fontSize: 25 }}
                                                                    className="text-[#247E45] group-hover/delete:text-red-600 transition-colors"
                                                                />
                                                            </button>
                                                            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                                                                <DialogTitle>Confirm Deletion</DialogTitle>
                                                                <DialogContent>Are you sure you want to delete this video?</DialogContent>
                                                                <DialogActions>
                                                                    <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">Cancel</Button>
                                                                    <Button onClick={confirmDelete} color="error">Delete</Button>
                                                                </DialogActions>
                                                            </Dialog>
                                                        </div>
                                                    </div>

                                                    {/* Video Thumbnail */}
                                                    <div
                                                        className="relative aspect-video overflow-hidden cursor-pointer group/thumb"
                                                        onClick={() => handlePlayClick(ele)}
                                                    >
                                                        {/* Thumbnail Image */}
                                                        <div
                                                            className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-500 group-hover/thumb:scale-110"
                                                            style={{
                                                                backgroundImage: `url(${thumbnailUrl})`,
                                                            }}
                                                        ></div>

                                                        {/* Loading Overlay */}
                                                        {isLoading && (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-slate-100 bg-opacity-75 backdrop-blur-sm">
                                                                <CircularProgress size={20} sx={{ color: '#247E45' }} />
                                                            </div>
                                                        )}

                                                        {/* Gradient Overlay */}
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-300"></div>

                                                        {/* Play Icon */}
                                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-all duration-300 transform group-hover/thumb:scale-110">
                                                            <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/50">
                                                                <svg className="w-8 h-8 text-white ml-1 filter drop-shadow-md" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M8 5v14l11-7z" />
                                                                </svg>
                                                            </div>
                                                        </div>

                                                        {/* Status Button Area */}
                                                        <div className="absolute bottom-0 left-0 right-0">
                                                            {ele.state === "reviewed" ? (
                                                                <Button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePlayClick(ele);
                                                                    }}
                                                                    className="w-full rounded-none bg-gradient-to-r from-[#247E45] to-[#026B33] text-white font-medium hover:from-[#026B33] hover:to-emerald-700 transition-all duration-300"
                                                                    style={{ textTransform: 'none', fontSize: '15px', padding: '8px' }}
                                                                >
                                                                    View Report
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    disabled={true}
                                                                    className="w-full rounded-none bg-slate-200 text-slate-500 font-medium"
                                                                    style={{ textTransform: 'none', fontSize: '15px', padding: '8px', opacity: 0.9 }}
                                                                >
                                                                    Waiting on Feedback
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* No Search Results Message */}
                                    {data?.length === 0 && searchTerm && (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-4">
                                                <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            </div>
                                            <p className="text-lg font-medium text-slate-600">No matching videos found</p>
                                            <p className="text-slate-400 text-sm mt-1">Try searching with a different keyword</p>
                                        </div>
                                    )}

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="flex justify-center mt-8">
                                            <SearchUploadedVideoPagination
                                                totalNumberofPages={totalPages}
                                                currentPage={currentPage}
                                                onPageChange={handlePageChange}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                filterOption === "reviewed" || filterOption === "favourites" ?
                                    <RenderNoVideosWithSearchCondition filterOption={filterOption} /> :
                                    <RenderNoVideosView />
                            )}
                        </>
                    )}
                    {!jwtToken && <RenderNoVideosView />}

                    {/* Feedback Modal */}
                    {selectedVideo && (
                        <Dialog
                            open={isModalOpen}
                            onClose={closeModal}
                            maxWidth={false}
                            fullScreen
                            sx={{
                                '& .MuiDialog-paper': {
                                    margin: 0,
                                    width: '100%',
                                    height: '100%',
                                    maxWidth: '100%',
                                    bgcolor: '#f8fafc'
                                },
                            }}
                        >
                            <DialogTitle className="flex justify-end p-4 border-b bg-white">
                                <IconButton
                                    edge="end"
                                    color="inherit"
                                    onClick={closeModal}
                                    aria-label="close"
                                    className="hover:bg-slate-100 transition-colors"
                                >
                                    <CloseIcon className="text-slate-600" />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent className="p-0 bg-slate-50">
                                <CoachFeedback
                                    videoId={selectedVideo._id}
                                />
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            {/* Custom Animations Styles */}
            <style jsx>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </>
    );
};

export default GameReports;
